// Dispatch component for the Workflow Hygiene drill-down tab
// (PRODOPSAI-385). One useApi instance whose fetcher switches based on the
// active source, so only the currently-selected drill-down fires a network
// request. Switching tabs (or filters, or page) triggers a single refetch
// via useApi's dependency array.
//
// Type narrowing: data can be one of three union arms. We narrow against
// `source` at render time to pass the right shape to the right table.

import { useState } from 'react';
import { portfolioApi } from '@/services/portfolio-api';
import { useApi } from '@/hooks/useApi';
import type { IMetricViewFilters } from '@/hooks/use-metric-view-filters';
import { ErrorBanner } from '@/components/ui';
import { UnlinkedItemsTable } from './UnlinkedItemsTable';
import { NonSpineStatusesTable } from './NonSpineStatusesTable';
import { NonCanonicalTypesTable } from './NonCanonicalTypesTable';
import type {
  IUnlinkedItemsDrillResponse,
  INonSpineStatusesDrillResponse,
  INonCanonicalTypesDrillResponse,
} from '@skystone/shared';

export type DrillSource = 'unlinked' | 'statuses' | 'types';

type DrillResponse =
  | IUnlinkedItemsDrillResponse
  | INonSpineStatusesDrillResponse
  | INonCanonicalTypesDrillResponse;

interface HygieneDrillTabProps {
  source: DrillSource;
  filters: IMetricViewFilters;
}

const PAGE_SIZE = 50;

export function HygieneDrillTab({ source, filters }: Readonly<HygieneDrillTabProps>) {
  const [page, setPage] = useState(1);

  const { data, loading, error } = useApi<DrillResponse>(
    () => {
      // Single-team scope is a hard requirement (the backend rejects calls
      // without it). When the user lands on a drill-down URL with no team
      // selected — e.g. a stale shared link — we skip the fetch entirely
      // and let the render guard below show the explanatory banner.
      if (!filters.teamId) {
        const empty: IUnlinkedItemsDrillResponse = {
          rows: [],
          pagination: { page, pageSize: PAGE_SIZE, totalCount: 0 },
        };
        return Promise.resolve(empty);
      }
      const params = {
        from: filters.from,
        to: filters.to,
        productId: filters.productId || undefined,
        teamId: filters.teamId,
        page,
        pageSize: PAGE_SIZE,
      };
      if (source === 'unlinked') return portfolioApi.getUnlinkedItemsDrill(params);
      if (source === 'statuses') return portfolioApi.getNonSpineStatusesDrill(params);
      return portfolioApi.getNonCanonicalTypesDrill(params);
    },
    [source, page, filters.from, filters.to, filters.productId, filters.teamId],
  );

  if (!filters.teamId) {
    return (
      <ErrorBanner message="Workflow Hygiene drill-down requires a single team to be selected." />
    );
  }

  // The useApi typing widens to the DrillResponse union; we cast to the
  // specific arm at the render seam since `source` discriminates which
  // backend method ran. Emptied placeholder shapes are typed inline so
  // TypeScript doesn't infer a wider union from a shared helper.
  const placeholderPagination = { page, pageSize: PAGE_SIZE, totalCount: 0 };

  if (source === 'unlinked') {
    const safe: IUnlinkedItemsDrillResponse =
      data ?? { rows: [], pagination: placeholderPagination };
    return <UnlinkedItemsTable data={safe} loading={loading} error={error} onPageChange={setPage} />;
  }

  if (source === 'statuses') {
    const safe: INonSpineStatusesDrillResponse =
      (data as INonSpineStatusesDrillResponse | null) ?? { rows: [], pagination: placeholderPagination };
    return <NonSpineStatusesTable data={safe} loading={loading} error={error} onPageChange={setPage} />;
  }

  const safe: INonCanonicalTypesDrillResponse =
    data ?? { rows: [], pagination: placeholderPagination };
  return <NonCanonicalTypesTable data={safe} loading={loading} error={error} onPageChange={setPage} />;
}
